import React, { useCallback, useEffect, useState } from 'react';
import { getHoldings } from '../../data/portfolio';
import {
    createNote,
    deleteNote,
    dismissNotice,
    getNotes,
    restoreNotice,
    updateNote,
} from '../../data/notes';
import PortfolioTabs from '../portfolio/PortfolioTabs';
import PortfolioHeader from '../portfolio/PortfolioHeader';
import { Skeleton } from '../portfolio/Loading';
import { translateNotice } from '../portfolio/notices';
import { useT } from '../../i18n';

// Notices and notes.
//
// Notices are the warnings the portfolio generates — a rejected connection, a
// currency that could not be converted. They used to sit at the top of every
// page, where a long-standing one trains the reader to ignore the whole block.
// Here they can be dismissed, and dismissal is org-wide: this is one shared
// portfolio and one reader clearing a notice should clear it for everybody.
//
// Notices are read from the CACHED holdings snapshot, never a forced refresh —
// opening this tab should not trigger a twenty-second Flex fetch.

const LOAD_STATE = { LOADING: 'loading', READY: 'ready', ERROR: 'error' };

export default function Notes() {
    // `when` used to format with an undefined locale; dates now follow the toggle.
    const { t, fmt } = useT();
    const [state, setState] = useState(LOAD_STATE.LOADING);
    const [notes, setNotes] = useState([]);
    const [dismissals, setDismissals] = useState([]);
    const [notices, setNotices] = useState([]);
    const [error, setError] = useState(null);

    const [draft, setDraft] = useState('');
    const [editing, setEditing] = useState(null);   // { id, body }
    const [busy, setBusy] = useState(false);
    const [showDismissed, setShowDismissed] = useState(false);

    const load = useCallback(async () => {
        const [noteData, holdings] = await Promise.all([
            getNotes(),
            // Cached — this tab must not trigger a broker fetch.
            getHoldings().catch(() => null),
        ]);
        setNotes(noteData.notes ?? []);
        setDismissals(noteData.dismissals ?? []);
        setNotices(holdings?.warnings ?? []);
    }, []);

    useEffect(() => {
        // StrictMode double-invokes effects in development. The flag stops a
        // late response from a discarded mount overwriting the live one.
        let cancelled = false;
        load()
            .then(() => { if (!cancelled) setState(LOAD_STATE.READY); })
            .catch((err) => {
                if (cancelled) return;
                setError(err);
                setState(LOAD_STATE.ERROR);
            });
        return () => { cancelled = true; };
    }, [load]);

    /** Every mutation re-reads rather than patching local state: notes are
     *  shared, so the server's list is the truth and someone else may have
     *  changed it since this page loaded. */
    const run = async (fn) => {
        setBusy(true);
        try {
            await fn();
            await load();
            setError(null);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    const dismissedSet = new Set(dismissals.map((d) => d.notice));
    const liveNotices = notices.filter((n) => !dismissedSet.has(n.trim()));

    return (
        <section className="holdings__sec">
            <div className="wrapper">
                <PortfolioHeader title={t('notes.title')} />

                <PortfolioTabs />

                {state === LOAD_STATE.LOADING && <Skeleton rows={8} />}

                {state === LOAD_STATE.ERROR && (
                    <p className="holdings__error" role="alert">
                        {error?.message || t('notes.error')}
                    </p>
                )}

                {state === LOAD_STATE.READY && (
                    <>
                        {error && (
                            <p className="holdings__error" role="alert">{error.message}</p>
                        )}

                        <h3 className="notes__h">{t('notes.noticesHeading')}</h3>
                        {liveNotices.length === 0 ? (
                            <p className="notes__empty">{t('notes.noticesEmpty')}</p>
                        ) : (
                            <ul className="notes__notices">
                                {liveNotices.map((notice) => (
                                    <li key={notice}>
                                        <span>{translateNotice(t, notice)}</span>
                                        <button
                                            type="button"
                                            className="notes__link"
                                            disabled={busy}
                                            onClick={() => run(() => dismissNotice(notice))}
                                        >
                                            {t('notes.dismiss')}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {dismissals.length > 0 && (
                            <>
                                <button
                                    type="button"
                                    className="notes__link notes__toggle"
                                    onClick={() => setShowDismissed((v) => !v)}
                                >
                                    {t(showDismissed ? 'notes.hideDismissed' : 'notes.showDismissed', { count: dismissals.length })}
                                </button>
                                {showDismissed && (
                                    <ul className="notes__notices is-dismissed">
                                        {dismissals.map((d) => (
                                            <li key={d.fingerprint}>
                                                <span>{translateNotice(t, d.notice)}</span>
                                                <button
                                                    type="button"
                                                    className="notes__link"
                                                    disabled={busy}
                                                    onClick={() => run(() => restoreNotice(d.fingerprint))}
                                                >
                                                    {t('notes.restore')}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </>
                        )}

                        <h3 className="notes__h">{t('notes.heading')}</h3>

                        <form
                            className="notes__compose"
                            onSubmit={(e) => {
                                e.preventDefault();
                                if (!draft.trim()) return;
                                run(async () => {
                                    await createNote(draft.trim());
                                    setDraft('');
                                });
                            }}
                        >
                            <textarea
                                className="notes__input"
                                placeholder={t('notes.placeholder')}
                                rows={3}
                                maxLength={4000}
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                aria-label={t('notes.newAria')}
                            />
                            <button type="submit" className="css_btn" disabled={busy || !draft.trim()}>
                                <span>{t('notes.add')}</span>
                            </button>
                        </form>

                        {notes.length === 0 ? (
                            <p className="notes__empty">{t('notes.empty')}</p>
                        ) : (
                            <ul className="notes__list">
                                {notes.map((note) => (
                                    <li key={note.id} className="notes__note">
                                        {editing?.id === note.id ? (
                                            <>
                                                <textarea
                                                    className="notes__input"
                                                    rows={3}
                                                    maxLength={4000}
                                                    value={editing.body}
                                                    onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                                                    aria-label={t('notes.editAria')}
                                                />
                                                <div className="notes__actions">
                                                    <button
                                                        type="button"
                                                        className="notes__link"
                                                        disabled={busy || !editing.body.trim()}
                                                        onClick={() => run(async () => {
                                                            await updateNote(note.id, editing.body.trim());
                                                            setEditing(null);
                                                        })}
                                                    >
                                                        {t('common.save')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="notes__link"
                                                        onClick={() => setEditing(null)}
                                                    >
                                                        {t('common.cancel')}
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <p className="notes__body">{note.body}</p>
                                                <div className="notes__meta">
                                                    <span>
                                                        {note.createdBy}
                                                        {fmt.date(note.createdAt) && ` · ${fmt.date(note.createdAt)}`}
                                                        {note.updatedAt !== note.createdAt && ` · ${t('notes.edited')}`}
                                                    </span>
                                                    <span className="notes__actions">
                                                        <button
                                                            type="button"
                                                            className="notes__link"
                                                            onClick={() => setEditing({ id: note.id, body: note.body })}
                                                        >
                                                            {t('common.edit')}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="notes__link is-danger"
                                                            disabled={busy}
                                                            onClick={() => run(() => deleteNote(note.id))}
                                                        >
                                                            {t('common.delete')}
                                                        </button>
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </div>
        </section>
    );
}
